import React, { useCallback, useEffect, useMemo, useState, KeyboardEvent, ChangeEvent } from "react";
import { useLocation } from "react-router-dom";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import { messageHandler } from "utils/messageHandler";
import { TEAMMEMBER_MESSAGES } from "utils/messages";
import { Row } from "@tanstack/react-table";
import TablePaginationControls from "components/TableandmobilePaginationcontrol";
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import {
  DesktopTableLoader,
  MobileCardsLoader,
  DataTableStatus,
} from "components/DataTableLoader";
import { ExportAsExcel } from "@siamf/react-export";
import { ViewIconButton, EditIconButton, DeleteIconButton } from "components/DatatablesActionIcons";
import DeleteConfirmModal from "components/ToolTips/DeleteConfirmModal";
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
} from "@tanstack/react-table";
import { getAuthCookie } from "../../utils/authCookies";
import { resolveSignageSessionOrganizationId } from "../../api/salesOrderApi";
import BrokerageTeammemberCreationComponent from "components/BrokerageTeammemberCreationComponent";

type BrokerageTeamMemberRow = {
  _id: string;
  id?: string;
  membership_id?: string;
  membershipType?: string;
  membership_type?: string;
  global_user_id?: string;
  firstName: string;
  lastName: string;
  email: string;
  jobTitle?: string;
  status?: string;
  licenseType?: string;
  brokerageFirmID?: string;
  brokerageFirmName?: string;
  brokerage_organization_id?: string;
  organization_id?: string;
  organization?: { id?: string; _id?: string };
  companyName?: string;
  brokerage_firm?:
    | string
    | {
        id?: string;
        name?: string;
        licenseType?: string;
        status?: string;
      };
  brokerage_firm_id?: string;
  officeNames?: string;
  offices?: { officeName?: string }[] | null;
  created_at?: string;
};

type BrokerageFirmOption = { value: string; label: string };

const BROKERAGE_PAGE_SIZE = 10;

const normalizeBrokerageId = (row: Record<string, unknown>) =>
  String(row?.value ?? row?.id ?? row?._id ?? "").trim();

interface ExportRow {
  "Brokerage Firm": string;
  "First Name": string;
  "Last Name": string;
  Role: string;
  Email: string;
  "Assigned Offices": string;
  Status: string;
}

function fuzzyTextFilter<TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: string
) {
  const raw = row.getValue(columnId);
  const value = (typeof raw === "boolean"
    ? raw
      ? "Active"
      : "Inactive"
    : String(raw)
  ).toLowerCase();

  return value.includes(filterValue.toLowerCase());
}

function getMemberId(member: BrokerageTeamMemberRow): string {
  return String(member.membership_id || member._id || member.id || "").trim();
}

function isBrokerageOwner(member: BrokerageTeamMemberRow): boolean {
  const membershipType = String(
    member.membershipType || member.membership_type || ""
  ).toUpperCase();
  return membershipType === "BROKERAGE_OWNER";
}

function getOwnerOrganizationId(member: BrokerageTeamMemberRow): string {
  return String(
    member.organization_id ||
      member.organization?.id ||
      member.organization?._id ||
      member.brokerage_organization_id ||
      ""
  ).trim();
}

function getFirmId(member: BrokerageTeamMemberRow): string {
  if (member.brokerageFirmID) return member.brokerageFirmID;
  if (member.brokerage_organization_id) return member.brokerage_organization_id;
  if (member.brokerage_firm_id) return member.brokerage_firm_id;

  const brokerageFirm = member.brokerage_firm;
  if (brokerageFirm && typeof brokerageFirm === "object") {
    return brokerageFirm.id || "";
  }

  return member.organization_id || "";
}

function getBrokerageFirmName(member: BrokerageTeamMemberRow): string {
  if (member.brokerageFirmName?.trim()) return member.brokerageFirmName.trim();
  if (member.companyName?.trim()) return member.companyName.trim();

  const brokerageFirm = member.brokerage_firm;
  if (typeof brokerageFirm === "string" && brokerageFirm.trim()) {
    return brokerageFirm.trim();
  }
  if (brokerageFirm && typeof brokerageFirm === "object" && brokerageFirm.name?.trim()) {
    return brokerageFirm.name.trim();
  }

  return "";
}

function getAssignedOffices(member: BrokerageTeamMemberRow): string {
  if (member.officeNames?.trim()) {
    return member.officeNames.trim();
  }
  if (Array.isArray(member.offices) && member.offices.length > 0) {
    return member.offices.map((o) => o.officeName).filter(Boolean).join(", ");
  }
  return "-";
}

const columnHelper = createColumnHelper<BrokerageTeamMemberRow>();

const SignageBrokerageTeammembers = () => {
  const [teammembers, setTeammembers] = useState<BrokerageTeamMemberRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
  const organizationId = getAuthCookie("organizationId");
  const location = useLocation();
  const isSignageTeammember = location.pathname.includes("/signageteammember");
  const baseRoute = isSignageTeammember ? "/signageteammember" : "/signage";
  const effectiveSignageOrgId =
    resolveSignageSessionOrganizationId() || organizationId || userId || "";
  const signageFirmId = effectiveSignageOrgId;

  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [globalFilter, setGlobalFilter] = useState("");
  const [exportRows, setExportRows] = useState<ExportRow[]>([]);
  const [searchInput, setSearchInput] = useState<string>(globalFilter ?? "");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [brokerageFirmFilter, setBrokerageFirmFilter] = useState("");
  const [selectedBrokerage, setSelectedBrokerage] = useState<BrokerageFirmOption | null>(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleteTargetName, setDeleteTargetName] = useState("");
  const [openAddTeammemberModal, setOpenAddTeammemberModal] = useState(false);
  const [teammemberRefreshKey, setTeammemberRefreshKey] = useState(0);

  const loadBrokerageOptions: LoadOptions<BrokerageFirmOption, any, { page: number }> =
    useCallback(
      async (inputValue, _loadedOptions, additional) => {
        const page = additional?.page ?? 1;

        if (!organizationId || !token) {
          return { options: [], hasMore: false, additional: { page } };
        }

        try {
          const q = (inputValue || "").trim();

          const params = new URLSearchParams({
            licenseType: "BROKERAGE_FIRM",
            query: q,
            page: String(page),
            limit: String(BROKERAGE_PAGE_SIZE),
          });

          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/organizations/${organizationId}/memberships/select?${params}`,
            {
              headers: {
                Accept: "application/json",
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const errorMessage =
              (errorData as { error?: string; message?: string })?.error ||
              (errorData as { error?: string; message?: string })?.message ||
              "Failed to fetch brokerage firms";
            console.error("Failed to fetch brokerage firms:", errorMessage);
            return { options: [], hasMore: false, additional: { page } };
          }

          const json = await response.json();
          const list = Array.isArray(json?.data) ? json.data : [];

          const options: BrokerageFirmOption[] = list.map((item: Record<string, unknown>) => {
            const companyName = String(
              (item.firmDetails as { companyName?: string } | undefined)?.companyName ||
                item.company_name ||
                item.label ||
                item.text ||
                "(no name)"
            );
            return {
              value: normalizeBrokerageId(item),
              label: companyName,
            };
          });

          const currentPage = Number(json?.page ?? page);
          const totalPages = Number(json?.totalPages ?? currentPage);
          const hasMore = currentPage < totalPages && list.length >= BROKERAGE_PAGE_SIZE;

          return {
            options,
            hasMore,
            additional: { page: page + 1 },
          };
        } catch (err) {
          console.error("Failed to load brokerage options:", err);
          return { options: [], hasMore: false, additional: { page } };
        }
      },
      [token, organizationId]
    );

  const handleBrokerageChange = (opt: BrokerageFirmOption | null) => {
    setSelectedBrokerage(opt);
    setBrokerageFirmFilter(opt ? opt.label : "");
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const handleAddTeammember = () => {
    setOpenAddTeammemberModal(true);
  };

  const handleAddTeammemberModalClose = () => {
    setOpenAddTeammemberModal(false);
  };

  const handleAddTeammemberSuccess = () => {
    setOpenAddTeammemberModal(false);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    setTeammemberRefreshKey((k) => k + 1);
  };

  useEffect(() => {
    const fetchBrokerageTeammembers = async () => {
      if (!signageFirmId) return;
      setLoading(true);
      try {
        const response = await fetch(
          // `${process.env.REACT_APP_API_URL}/users/signage/${signageFirmId}/brokerage-team-members/datatable`,
          `${process.env.REACT_APP_API_URL}/organizations/${organizationId}/brokerage-team-members/datatable`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              page: pagination.pageIndex + 1,
              limit: pagination.pageSize,
              search: globalFilter,
              filters: {
                ...(brokerageFirmFilter ? { brokerage_firm: brokerageFirmFilter } : {}),
              },
              sort: {
                field: "created_at",
                order: "desc",
              },
              fields: [
                "id",
                "firstName",
                "lastName",
                "email",
                "brokerage_firm",
                "brokerage_firm_id",
                "jobTitle",
                "officeNames",
                "status",
                "created_at",
              ],
            }),
          }
        );
        const result: { data?: BrokerageTeamMemberRow[]; total?: number; error?: string } =
          await response.json();
        if (!response.ok || result?.error) {
          messageHandler.error(
            "teammember",
            "fetch",
            new Error(TEAMMEMBER_MESSAGES.fetch.error)
          );
          throw new Error(TEAMMEMBER_MESSAGES.fetch.error);
        }
        const members = result.data ?? [];
        setTeammembers(members);
        setRowCount(result?.total ?? 0);
      } catch (err) {
        console.error("Failed to load brokerage team members.", err);
      } finally {
        setLoading(false);
      }
    };

    if (token && signageFirmId) {
      fetchBrokerageTeammembers();
    }
  }, [
    token,
    signageFirmId,
    pagination,
    globalFilter,
    deleting,
    brokerageFirmFilter,
    selectedBrokerage,
    teammemberRefreshKey,
  ]);

  const handleDeleteTeammember = async (membershipId: string) => {
    setDeleting(membershipId);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/organization-memberships/${membershipId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const result: { error?: string } = await response.json().catch(() => ({}));
      if (!response.ok || result?.error) {
        messageHandler.error(
          "teammember",
          "delete",
          new Error(TEAMMEMBER_MESSAGES.delete.error)
        );
        throw new Error(TEAMMEMBER_MESSAGES.delete.error);
      }
      messageHandler.success("teammember", "delete");
      setTeammembers((prev) =>
        prev.filter((tm) => getMemberId(tm) !== membershipId)
      );
      setRowCount((prev) => Math.max(0, prev - 1));
    } catch {
      console.error("Failed to delete the team member.");
    } finally {
      setDeleting(null);
      setOpenDelete(false);
      setDeleteTargetId(null);
      setDeleteTargetName("");
    }
  };

  const handleDeleteClick = (member: BrokerageTeamMemberRow) => {
    const membershipId = getMemberId(member);
    if (!membershipId) {
      messageHandler.error(
        "teammember",
        "delete",
        new Error("Membership ID is missing for this team member.")
      );
      return;
    }
    setDeleteTargetId(membershipId);
    setDeleteTargetName(
      `${member.firstName ?? ""} ${member.lastName ?? ""}`.trim() || "Team member"
    );
    setOpenDelete(true);
  };

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "serial",
        header: () => <div className="th-text S-No">S.No.</div>,
        cell: (info) => (
          <div className="text-sm S-No">
            {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
          </div>
        ),
      }),

      columnHelper.accessor(
        (row: BrokerageTeamMemberRow) => getBrokerageFirmName(row),
        {
          id: "brokerageFirm",
          header: () => <div className="th-text">Brokerage Firm</div>,
          cell: (info) => <span className="text-sm">{info.getValue()}</span>,
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),

      columnHelper.accessor((row: BrokerageTeamMemberRow) => row.firstName, {
        id: "firstName",
        header: () => <div className="th-text">First Name</div>,
        cell: (info) => <span className="text-sm">{info.getValue()}</span>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),

      columnHelper.accessor((row: BrokerageTeamMemberRow) => row.lastName, {
        id: "lastName",
        header: () => <div className="th-text">Last Name</div>,
        cell: (info) => <span className="text-sm">{info.getValue()}</span>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),

      columnHelper.accessor((row: BrokerageTeamMemberRow) => row?.jobTitle || "-", {
        id: "role",
        header: () => <div className="th-text">Role</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
        cell: (info) => <span className="text-sm">{info.getValue()}</span>,
      }),

      columnHelper.accessor((row: BrokerageTeamMemberRow) => row?.email, {
        id: "email",
        header: () => <div className="th-text">Email</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
        cell: (info) => <span className="text-sm">{info.getValue()}</span>,
      }),

      columnHelper.accessor(
        (row: BrokerageTeamMemberRow) => getAssignedOffices(row),
        {
          id: "offices",
          header: () => <div className="th-text">Assigned Offices</div>,
          cell: (info) => <span className="text-sm">{info.getValue()}</span>,
          enableGlobalFilter: true,
        }
      ),

      columnHelper.accessor("status", {
        header: () => <div className="th-text  text-center">Status</div>,
        cell: (info) => {
          const isActive =
            String(info.getValue() || "").trim().toLowerCase() === "active";
          const className = isActive
            ? "status-radio status-radio-active"
            : "status-radio status-radio-inactive";

          return (
            <div className="d-flex justify-content-center w-100 table-cells text-center">
              <input type="radio" className={className} checked readOnly />
            </div>
          );
        },
        enableGlobalFilter: false,
      }),

      columnHelper.display({
        id: "actions",
        header: () => <div className="th-text">Actions</div>,
        cell: (info) => {
          const member = info.row.original;
          const memberId = getMemberId(member);
          const firmId = getFirmId(member);
          const owner = isBrokerageOwner(member);
          const ownerOrgId = getOwnerOrganizationId(member);
          const viewHref = owner
            ? `${baseRoute}/brokerageFirm/${ownerOrgId}`
            : `${baseRoute}/view-teammember/${firmId}/${memberId}`;
          const editHref = owner
            ? `${baseRoute}/brokerageFirm/${ownerOrgId}/edit`
            : `${baseRoute}/brokerageTeammember/${firmId}/${memberId}`;
          const canNavigate = owner
            ? Boolean(ownerOrgId)
            : Boolean(firmId && memberId);

          return (
            <div className="d-flex align-items-center gap-2">
              <ViewIconButton
                to={viewHref}
                state={owner ? undefined : { type: "office-teammber" }}
                className="btn action-btn fs-2 text-center"
                title="View"
                disabled={!canNavigate}
              />
              <EditIconButton
                to={editHref}
                className="icon-square"
                title="Edit"
                disabled={!canNavigate}
              />
              <DeleteIconButton
                className="icon-square danger"
                title="Delete"
                onClick={() => handleDeleteClick(member)}
                disabled={deleting === memberId}
              />
            </div>
          );
        },
      }),
    ],
    [deleting, pagination, baseRoute]
  );

  const table = useReactTable({
    data: teammembers,
    columns,
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    state: {
      pagination,
      globalFilter,
    },
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
  });

  useEffect(() => {
    const rows: ExportRow[] = teammembers.map((member) => ({
      "Brokerage Firm": getBrokerageFirmName(member),
      "First Name": member.firstName || "",
      "Last Name": member.lastName || "",
      Role: member.jobTitle || "",
      Email: member.email || "",
      "Assigned Offices": getAssignedOffices(member),
      Status: member.status || "",
    }));
    setExportRows(rows);
  }, [teammembers]);

  return (
    <div className="content">
      <div className="d-flex justify-content-between align-items-center pb-3 mb-2 py-2">
        <div className="d-flex gap-2">
          <h3 className="m-0 fw-bold">Brokerage Teammembers</h3>
        </div>
        <div className="d-flex gap-3 align-items-center">
          <button
            type="button"
            className="btn btn-primary table-addbutton"
            onClick={handleAddTeammember}
          >
            + Add Brokerage Teammember
          </button>
        </div>
      </div>

      <div className="po-header mb-4 shadow-border-datatables ">
        <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
          <div className="po-title-wrap me-2 d-flex align-items-center">
            <i className="fs-24 ri-search-line text-primary" />
            <span className=".po-title-search fs-19 fw-normal ms-2">Search</span>
          </div>

          <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
            <form
              className="input-group po-search"
              autoComplete="off"
              onSubmit={(e) => {
                e.preventDefault();
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                setGlobalFilter(searchInput);
              }}
            >
              <span className="input-group-text">
                <i className="ri-search-line" />
              </span>
              <input
                type="text"
                id="signageBrokerageTeammembersSearchInput"
                name="signage_brokerage_teammembers_datatable_search"
                className="form-control"
                placeholder="Search by Name and Email"
                value={searchInput}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Enter") {
                    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                    setGlobalFilter(searchInput);
                  }
                }}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
              />
            </form>

            <button
              type="button"
              className={` po-filter-btn d-inline-flex align-items-center ${filtersOpen ? "active" : ""}`}
              onClick={() => setFiltersOpen((v) => !v)}
              aria-expanded={filtersOpen}
              aria-controls="brokerage-teammembers-filters"
            >
              <i className="ri-filter-3-line me-2" />
              <span>Filters</span>
              <i className={`ri-arrow-down-s-line ms-1 ${filtersOpen ? "rotate-180" : ""}`} />
            </button>

            {/* <ExportAsExcel
              data={exportRows}
              headers={[
                "Brokerage Firm",
                "First Name",
                "Last Name",
                "Role",
                "Email",
                "Assigned Offices",
                "Status",
              ]}
              fileName="BrokerageTeammembers"
            >
              {(props) => (
                <button {...props} className="export-button">
                  <i className="ri-file-excel-2-line me-2" />
                  Export to Excel
                </button>
              )}
            </ExportAsExcel> */}
          </div>
        </div>

        <div
          id="brokerage-teammembers-filters"
          className={`po-filters ${filtersOpen ? "show" : ""}`}
        >
          <div className="po-filters-bar d-flex">
            <div className="row g-2 g-md-3 align-items-end justify-content-md-end ms-md-auto w-100 w-md-auto">
              <div className="col-auto">
                <div className="po-title-wrap me-2 d-flex align-items-center justify-content-end brokerage-teammember-firm-select">
                  <AsyncPaginate
                    classNamePrefix="rs"
                    placeholder="Search brokerage firms..."
                    value={selectedBrokerage}
                    loadOptions={loadBrokerageOptions}
                    onChange={handleBrokerageChange}
                    additional={{ page: 1 }}
                    debounceTimeout={300}
                    isClearable
                    menuPlacement="bottom"
                    styles={{
                      control: (provided, state) => ({
                        ...provided,
                        minHeight: "38px",
                        border: state.isFocused
                          ? "1px solid #86b7fe"
                          : "1px solid #ced4da",
                        borderRadius: "0.375rem",
                        boxShadow: state.isFocused
                          ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)"
                          : "none",
                        "&:hover": {
                          borderColor: "#86b7fe",
                        },
                      }),
                      placeholder: (provided) => ({
                        ...provided,
                        color: "#6c757d",
                      }),
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 9999,
                        border: "1px solid #dee2e6",
                        borderRadius: "0.375rem",
                        boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.15)",
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isSelected
                          ? "#0d6efd"
                          : state.isFocused
                            ? "#e7f1ff"
                            : "white",
                        color: state.isSelected ? "white" : "#212529",
                        "&:hover": {
                          backgroundColor: state.isSelected ? "#0d6efd" : "#e7f1ff",
                        },
                      }),
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
        <div className="align-items-center bg-white border-bottom  d-flex justify-content-between px-4 py-4">
          <h5 className="mb-0 "></h5>
        </div>
        <hr className="mb-2" style={{ borderTop: "none" }} />
        <div className="rdt-desktop ">
          <div
            className="production-scroll"
            style={{ "--rdt-min": "1400px" } as React.CSSProperties}
          >
            <table className="table production-table align-middle mb-0 table-1">
              {loading ? (
                <DesktopTableLoader table={table} message="Loading team members…" variant="dots" />
              ) : (
                <DesktopTableParts table={table} />
              )}
            </table>
          </div>
        </div>

        <div className="rdt-mobile">
          {loading ? (
            <MobileCardsLoader message="Loading team members…" variant="dots" />
          ) : (
            <MobileCardsOnly
            table={table}
            idColId="order_link"
            titleColId="firstName"
            actionColId="actions"
          />
          )}
        </div>
      </div>

      <DataTableStatus
        loading={loading}
        isEmpty={table.getRowModel().rows.length === 0}
      >
        <div className="mt-4 mb-4 ">
          <TablePaginationControls table={table} />
        </div>
      </DataTableStatus>

      <DeleteConfirmModal
        open={openDelete}
        dynamicName={deleteTargetName || "Team member"}
        onCancel={() => {
          setOpenDelete(false);
          setDeleteTargetId(null);
          setDeleteTargetName("");
        }}
        onDelete={() => {
          if (deleteTargetId) {
            void handleDeleteTeammember(deleteTargetId);
          }
        }}
      />

      {openAddTeammemberModal ? (
        <div
          className="modal fade show d-block brokerage-teammember-add-modal"
          tabIndex={-1}
          role="dialog"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1050,
            overflow: "hidden",
          }}
          onClick={handleAddTeammemberModalClose}
        >
          <div
            className="modal-dialog brokerage-teammember-add-modal-dialog modal-dialog-scrollable"
            role="document"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content brokerage-teammember-add-modal-content">
              <div className="modal-header border-bottom flex-shrink-0">
                <h5 className="modal-title fw-bold mb-0">Add Brokerage Teammember</h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={handleAddTeammemberModalClose}
                />
              </div>
              <div className="modal-body brokerage-teammember-add-modal-body p-0">
                <BrokerageTeammemberCreationComponent
                  variant="modal"
                  firmId={selectedBrokerage?.value}
                  onClose={handleAddTeammemberModalClose}
                  onSuccess={handleAddTeammemberSuccess}
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SignageBrokerageTeammembers;
